import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { BUSINESS } from "../data/business";
import { services } from "../data/services";
import { locations } from "../data/locations";

export default function Header() {
  const [open, setOpen] = useState(false);
  const [openMenu, setOpenMenu] = useState(null);
  const loc = useLocation();

  React.useEffect(() => { setOpen(false); setOpenMenu(null); }, [loc.pathname]);

  return (
    <header className="sticky top-0 z-50 bg-cream/85 backdrop-blur-xl border-b border-lc" data-testid="site-header">
      <div className="max-w-7xl mx-auto px-5 lg:px-8 h-16 lg:h-20 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
          <img src={BUSINESS.logo} alt="Last Cut Landscaping" className="h-10 lg:h-12 w-auto" />
          <div className="hidden sm:block leading-tight ml-1">
            <div className="font-display text-[15px] lg:text-base font-semibold text-dark tracking-tight">Last&nbsp;Cut</div>
            <div className="text-[10px] tracking-[0.18em] uppercase text-dark/60 font-semibold mt-0.5">Landscaping</div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-1" aria-label="Primary">
          <NavItem to="/" label="Home" />
          <DropDown label="Services" items={services.map((s) => ({ label: s.name, to: `/services/${s.slug}` }))} open={openMenu === "s"} onToggle={() => setOpenMenu(openMenu === "s" ? null : "s")} />
          <DropDown label="Areas" items={locations.map((l) => ({ label: l.name, to: `/locations/${l.slug}` }))} open={openMenu === "l"} onToggle={() => setOpenMenu(openMenu === "l" ? null : "l")} />
          <NavItem to="/projects" label="Projects" />
          <NavItem to="/blog" label="Blog" />
          <NavItem to="/about" label="About" />
          <NavItem to="/contact" label="Contact" />
        </nav>

        <div className="flex items-center gap-2">
          <a href={`tel:${BUSINESS.phoneDial}`} className="hidden md:inline-flex items-center gap-2 text-sm font-semibold text-dark" data-testid="header-call">
            <Phone size={16} /> {BUSINESS.phone}
          </a>
          <Link to="/quote" className="btn-accent text-sm hidden sm:inline-flex" data-testid="header-quote-btn">Get Free Quote</Link>
          <button className="lg:hidden p-2 -mr-2" onClick={() => setOpen(!open)} aria-label="Menu" data-testid="mobile-menu-toggle">
            {open ? <X /> : <Menu />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden bg-cream border-t border-lc max-h-[80vh] overflow-y-auto" data-testid="mobile-nav">
          <div className="px-5 py-4 grid gap-1">
            <MLink to="/">Home</MLink>
            <details><summary className="py-2.5 font-semibold cursor-pointer">Services</summary>
              <div className="grid gap-1 pl-3 pb-2">{services.map((s) => <MLink key={s.slug} to={`/services/${s.slug}`}>{s.name}</MLink>)}</div>
            </details>
            <details><summary className="py-2.5 font-semibold cursor-pointer">Areas</summary>
              <div className="grid gap-1 pl-3 pb-2">{locations.map((l) => <MLink key={l.slug} to={`/locations/${l.slug}`}>{l.name}</MLink>)}</div>
            </details>
            <MLink to="/projects">Projects</MLink>
            <MLink to="/blog">Blog</MLink>
            <MLink to="/about">About</MLink>
            <MLink to="/contact">Contact</MLink>
            <Link to="/quote" className="btn-accent mt-3" data-testid="mobile-quote-btn">Get Free Quote</Link>
          </div>
        </div>
      )}
    </header>
  );
}

const NavItem = ({ to, label }) => (
  <Link to={to} className="px-3 py-2 text-sm font-semibold text-dark/80 hover:text-dark rounded-full hover:bg-paper transition" data-testid={`nav-${label.toLowerCase()}`}>{label}</Link>
);

const MLink = ({ to, children }) => (
  <Link to={to} className="py-2.5 text-[15px] font-medium text-dark border-b border-lc/60 last:border-0">{children}</Link>
);

const DropDown = ({ label, items, open, onToggle }) => (
  <div className="relative">
    <button onClick={onToggle} className="px-3 py-2 text-sm font-semibold text-dark/80 hover:text-dark rounded-full hover:bg-paper transition" data-testid={`nav-${label.toLowerCase()}`}>{label} ▾</button>
    {open && (
      <div className="absolute left-0 top-full mt-2 bg-white rounded-2xl shadow-2xl border border-lc p-2 grid grid-cols-2 gap-1 w-[460px]">
        {items.map((it) => <Link key={it.to} to={it.to} className="px-3 py-2 text-sm rounded-lg hover:bg-paper text-dark">{it.label}</Link>)}
      </div>
    )}
  </div>
);
